import React, { useState } from 'react';
import { Button, Input } from '../ui';
import InscricaoService from '../../services/Inscricoes/InscricaoService';
import { Ticket, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const inscricaoService = new InscricaoService();

export function VoucherForm({ inscricaoId, corridaId, onSuccess, onError }) {
  const [codigoVoucher, setCodigoVoucher] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const voucherTrimmed = codigoVoucher.trim();
    if (!voucherTrimmed) {
      setErro('Por favor, informe o código do voucher.');
      return;
    }

    if (!inscricaoId) {
      setErro('Identificador da inscrição não encontrado.');
      return;
    }

    setIsLoading(true);
    setErro(null);

    try {
      const hoje = new Date();
      const year = hoje.getFullYear();
      const month = String(hoje.getMonth() + 1).padStart(2, '0');
      const day = String(hoje.getDate()).padStart(2, '0');
      const dataFormatada = `${year}-${month}-${day}`;

      const payload = {
        status: 'Inscrito',
        data_pagamento: dataFormatada,
        metodo_pagamento: 'CORTESIA',
        valor_pagamento: 0,
        nome_voucher: voucherTrimmed,
        voucher: voucherTrimmed,
        codigo_voucher: voucherTrimmed,
      };

      if (corridaId) {
        payload.id_corrida = corridaId;
      }

      // Chama o serviço de inscrição para aplicar o voucher
      const response = inscricaoService.aplicarVoucher
        ? await inscricaoService.aplicarVoucher(inscricaoId, payload)
        : await inscricaoService.updateInscricao(JSON.stringify(payload), inscricaoId);

      if (response && response.ok) {
        const data = await response.json().catch(() => ({}));
        setSucesso(true);

        const resultadoFormatado = {
          ...data,
          status: data.status || 'CONFIRMED',
          status_detail: 'accredited',
          titulo: 'Inscrição Confirmada!',
          mensagem: data.mensagem || 'Voucher aplicado com sucesso! Sua inscrição foi confirmada.',
          order_id: data.order_id || data.id || inscricaoId,
        };

        onSuccess?.(resultadoFormatado);
      } else {
        const errData = response ? await response.json().catch(() => ({})) : {};
        let msg = errData?.erro || errData?.detail || errData?.mensagem || errData?.detalhe;

        if (errData?.nome_voucher) {
          msg = Array.isArray(errData.nome_voucher)
            ? errData.nome_voucher.join(' ')
            : errData.nome_voucher;
        }

        if (!msg) {
          if (response?.status === 404) {
            msg = 'Inscrição não encontrada para efetivação com voucher.';
          } else {
            msg = 'Não foi possível validar este voucher. Verifique o código e tente novamente.';
          }
        }

        setErro(msg);
        onError?.({ status: response?.status || 400, data: errData, message: msg });
      }
    } catch (err) {
      console.error('Erro ao processar pagamento com voucher:', err);
      const msg = 'Erro de conexão ao aplicar o voucher. Verifique sua rede e tente novamente.';
      setErro(msg);
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (sucesso) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center gap-3 bg-urb-card border border-urb-border rounded-xl">
        <CheckCircle2 className="w-12 h-12 text-urb-primary animate-bounce" />
        <h3 className="text-lg font-bold text-white">Voucher Aplicado!</h3>
        <p className="text-xs text-slate-400">Sua inscrição foi confirmada com sucesso.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="p-6 bg-urb-card border border-urb-border rounded-xl flex flex-col gap-4 shadow-lg text-left"
    >
      <div className="flex items-center gap-3 pb-3 border-b border-urb-border">
        <div className="w-10 h-10 rounded-xl bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center text-urb-primary shrink-0">
          <Ticket className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-base font-bold text-white m-0">Pagamento com Voucher</h3>
          <p className="text-xs text-slate-400 m-0 mt-0.5">
            Insira o código do seu voucher promocional ou cortesia para quitar a inscrição.
          </p>
        </div>
      </div>

      {erro && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs sm:text-sm font-semibold flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{erro}</span>
        </div>
      )}

      <div>
        <label htmlFor="codigo-voucher-input" className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wider">
          Código do Voucher
        </label>
        <Input
          id="codigo-voucher-input"
          type="text"
          placeholder="Ex: CORTESIAVIP, PARCEIRO100"
          value={codigoVoucher}
          onChange={(e) => {
            setCodigoVoucher(e.target.value.toUpperCase());
            if (erro) setErro(null);
          }}
          disabled={isLoading}
          autoFocus
          className="uppercase tracking-widest font-mono font-bold"
        />
        <span className="text-[11px] text-slate-500 mt-1 block">
          O código é intransferível e tem limite de utilização de acordo com o organizador.
        </span>
      </div>

      <Button
        type="submit"
        variant="primary"
        disabled={isLoading || !codigoVoucher.trim()}
        className="w-full py-3 mt-1 font-bold text-sm flex items-center justify-center gap-2 shadow-neon uppercase"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Validando Voucher...</span>
          </>
        ) : (
          <>
            <Ticket className="w-4 h-4" />
            <span>Aplicar Voucher e Confirmar</span>
          </>
        )}
      </Button>
    </form>
  );
}

export default VoucherForm;
